import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion } from '@/common/components/Accordion/Accordion';
import { Badge } from '@/common/components/Badge/Badge';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { RadioButton } from '@/common/components/RadioButton/RadioButton';
import {
  TInsurancePlanSimulation,
  TMenhirInsurancePaymentMethod,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { buildTranslatedFaqs } from '@/common/utils/i18n';
import { moneyToString } from '@coverflex-tech/hypernova-utils';

const paymentMethodsFAQsMap = [
  {
    i18nKey:
      'dashboard.insurance.upgrade_plan.step.payment_method.faqs.wallet.list',
    combinations: ['wallet'],
  },
  {
    i18nKey:
      'dashboard.insurance.upgrade_plan.step.payment_method.faqs.payroll_all.list',
    combinations: ['wallet', 'payroll'],
  },
] satisfies {
  i18nKey: string;
  combinations: TMenhirInsurancePaymentMethod[];
}[];

export const PaymentMethodStep = ({
  simulation,
  setPaymentMethod,
  paymentMethod,
}: TPaymentMethodStepProps) => {
  const { t } = useTranslation();
  const { goNext } = useFlowLayout();
  const [error, setError] = useState<string>();

  const handleNext = () => {
    if (paymentMethod) {
      goNext();
    } else {
      setError(t('errors.input.required'));
    }
  };

  const i18nFAQsKey = paymentMethodsFAQsMap.find((item) => {
    return (
      simulation.holderPaymentMethodsAllowed?.length ===
        item.combinations.length &&
      simulation.holderPaymentMethodsAllowed?.every((p) =>
        item.combinations.some((c) => c === p)
      )
    );
  })?.i18nKey;

  const faqs = buildTranslatedFaqs(i18nFAQsKey ? i18nFAQsKey : '');

  return (
    <FlowLayout.Content primaryAction={{ onPress: handleNext }}>
      <RadioButton.Group
        variant="card"
        gap="2xs"
        onChange={(value) => {
          setPaymentMethod(value as TMenhirInsurancePaymentMethod);
        }}
        value={paymentMethod}
        error={error}
      >
        {simulation?.holderPaymentMethodsAllowed.map((method) => (
          <RadioButton.Option
            key={method}
            value={method}
            label={t(
              `dashboard.insurance.upgrade_plan.step.payment_method.payment_method.${method}.title`
            )}
            description={t(
              `dashboard.insurance.upgrade_plan.step.payment_method.payment_method.${method}.description`
            )}
          >
            {method === 'wallet' &&
            simulation.savingsAmount &&
            simulation.savingsAmount.amount > 0 ? (
              <Badge size="md" emphasis="normal" status="info">
                {t(
                  'dashboard.insurance_change_payment.choose_payment_method.benefits_badge.label',
                  { savings: moneyToString(simulation.savingsAmount) }
                )}
              </Badge>
            ) : null}
          </RadioButton.Option>
        ))}
      </RadioButton.Group>
      {simulation?.holderPaymentMethodsAllowed.includes('wallet') && (
        <BodyText size="sm" color="neutral20">
          {t(
            'dashboard.insurance_change_payment.choose_payment_method.estimated_savings.description'
          )}
        </BodyText>
      )}
      {faqs.length > 0 && (
        <Accordion.Root
          title={t(
            'dashboard.insurance.upgrade_plan.step.payment_method.faqs.title'
          )}
        >
          {faqs.map((faq, index) => (
            <Accordion.Item
              key={index}
              question={faq.question}
              answer={faq.answer}
            />
          ))}
        </Accordion.Root>
      )}
    </FlowLayout.Content>
  );
};

export type TPaymentMethodStepProps = {
  simulation: TInsurancePlanSimulation;
  setPaymentMethod: (paymentMethod: TMenhirInsurancePaymentMethod) => void;
  paymentMethod?: TMenhirInsurancePaymentMethod;
};
